import { type Dtos } from "@inrev/common";
import { Option } from "@inrev/core";
import {
	ActiveStatementTypeEnum,
	type AnalysisPeriodSelectorItem,
	type BalanceSheetPeriodSelectorItem,
	type IncomeStatementPeriodSelectorItem,
} from "../types";
import {
	balanceSheetSlotCounts,
	balanceSheetSlotKey,
	balanceSheetTypeLabel,
	incomeStatementSlotCounts,
	incomeStatementSlotKey,
	incomeStatementTypeLabel,
} from "./financialStatementSlots";

const activeBalanceSheetIds = (financialPeriods: Dtos.Admin.FinancialPeriod.Get.Response[]) =>
	new Set(financialPeriods.map((p) => p.activeBalanceSheetId).filter(Option.isSome));

const activeIncomeStatementIds = (financialPeriods: Dtos.Admin.FinancialPeriod.Get.Response[]) =>
	new Set(financialPeriods.map((p) => p.activeIncomeStatementId).filter(Option.isSome));

export const buildBalanceSheetPeriodSelectorItems = (
	balanceSheets: Dtos.Admin.BalanceSheet.Get.Response[],
	financialPeriods: Dtos.Admin.FinancialPeriod.Get.Response[],
): BalanceSheetPeriodSelectorItem[] => {
	const slotCounts = balanceSheetSlotCounts(balanceSheets);
	const activeIds = activeBalanceSheetIds(financialPeriods);

	return balanceSheets
		.filter((bs) => !!bs.view)
		.map((bs) => {
			const typeLabel = balanceSheetTypeLabel(bs.statementType);
			const key = balanceSheetSlotKey(bs);
			const hasSlotRevisions = (slotCounts.get(key) ?? 1) > 1;
			const isActive = activeIds.has(bs.id);

			return {
				type: ActiveStatementTypeEnum.BalanceSheet,
				id: bs.id,
				date: bs.statementDate,
				statementType: bs.statementType,
				typeLabel,
				preparationMethod: bs.preparationMethod,
				createdAt: bs.createdAt,
				showRevisionLabel: hasSlotRevisions,
				isActive,
				showActiveIndicator: isActive && hasSlotRevisions,
				sourceStatementDate: bs.sourceStatementDate,
				sourceDataSource: bs.sourceDataSource,
			};
		});
};

export const buildIncomeStatementPeriodSelectorItems = (
	incomeStatements: Dtos.Admin.IncomeStatement.Get.Response[],
	financialPeriods: Dtos.Admin.FinancialPeriod.Get.Response[],
): IncomeStatementPeriodSelectorItem[] => {
	const slotCounts = incomeStatementSlotCounts(incomeStatements);
	const activeIds = activeIncomeStatementIds(financialPeriods);

	return incomeStatements
		.filter((is) => !!is.view)
		.map((is) => {
			const typeLabel = incomeStatementTypeLabel(is.periodType);
			const key = incomeStatementSlotKey(is);
			const hasSlotRevisions = (slotCounts.get(key) ?? 1) > 1;
			const isActive = activeIds.has(is.id);
			return {
				type: ActiveStatementTypeEnum.IncomeStatement,
				id: is.id,
				date: is.periodEndDate,
				periodType: is.periodType,
				typeLabel,
				preparationMethod: is.preparationMethod,
				createdAt: is.createdAt,
				showRevisionLabel: hasSlotRevisions,
				isActive,
				showActiveIndicator: isActive && hasSlotRevisions,
				sourceStatementDate: is.sourceStatementDate,
				sourceDataSource: is.sourceDataSource,
			};
		});
};

/**
 * One row per financial period that has an active balance sheet, income statement, or both. Unlike
 * the per-statement selectors, this is keyed by financial period and carries flags for which
 * statements the period has.
 */
export const buildAnalysisPeriodSelectorItems = (
	financialPeriods: Dtos.Admin.FinancialPeriod.Get.Response[],
): AnalysisPeriodSelectorItem[] =>
	financialPeriods
		.filter(
			(p) => Option.isSome(p.activeBalanceSheetId) || Option.isSome(p.activeIncomeStatementId),
		)
		.map((p) => ({
			type: ActiveStatementTypeEnum.Analysis,
			id: p.id,
			date: p.periodEndDate,
			periodType: p.periodType,
			typeLabel: incomeStatementTypeLabel(p.periodType),
			preparationMethod: p.preparationMethod,
			createdAt: p.createdAt,
			showRevisionLabel: false,
			isActive: true,
			showActiveIndicator: false,
			balanceSheetId: p.activeBalanceSheetId,
			incomeStatementId: p.activeIncomeStatementId,
			hasBalanceSheet: Option.isSome(p.activeBalanceSheetId),
			hasIncomeStatement: Option.isSome(p.activeIncomeStatementId),
		}));
